/* ─────────────────────────────────────────────────────────────────────────────────────────────
   ts-carousel.js — the one-line scrolling row: the homepage review row, and the product pages' rows.

   ── WHY THIS IS AN ASSET AND NOT AN INLINE IIFE ────────────────────────────────────────────
   It used to live inline in sections/ts-home.liquid, bound to four hardcoded element ids
   (tsStylesRow / tsStylesPrev / tsStylesNext / tsStylesDots). That was fine while "Choose your
   style" was the only carousel on the page. The homepage now has four of them, and four copies
   of the same IIFE with four sets of ids is how the two style-card call sites drifted apart.

   So: one file, and instances are found by attribute. Everything inside init() is per-instance
   state — there is deliberately no module-level mutable state, because two rows resizing at the
   same time would share it.

   ── MARKUP CONTRACT ────────────────────────────────────────────────────────────────────────
     <div class="ts-carousel-wrap" data-ts-carousel data-ts-carousel-label="styles">
       <button data-ts-carousel-prev hidden>…</button>
       <button data-ts-carousel-next hidden>…</button>
       <div class="ts-lookgrid is-carousel" data-ts-carousel-row> …cards… </div>
       <div class="ts-carousel-dots" data-ts-carousel-dots hidden></div>
     </div>

   The row is a CSS scroll-snap container (.ts-lookgrid.is-carousel, in the CORE stylesheet since
   2026-09-13) and scrolls perfectly well with this file absent, at every width.
   Everything here is an affordance on top, which is why every control starts `hidden` and is only
   revealed once there is somewhere to scroll. A dead control is worse than none.
   ───────────────────────────────────────────────────────────────────────────────────────── */
(function () {
  /* German, and any later language, arrives as window.TS_I18N (snippets/ts-i18n.liquid); English keeps
     the words written below. data-ts-carousel-label stays an English word in the markup and is
     looked up here, so no page has to translate an attribute. */
  var I18N = window.TS_I18N || {};
  function tr(key, en, vars) {
    var s = I18N[key] || en;
    for (var k in vars || {}) s = s.split('{' + k + '}').join(vars[k]);
    return s;
  }
  function labelOf(l) { return (I18N.carousel_labels && I18N.carousel_labels[l]) || l; }
  function init(wrap) {
    /* Idempotent by flag, not by hope. boot() runs once, but shopify:section:load fires again for
       any section the theme editor re-renders, and a second pass over the same element would stack
       a second scroll listener and a second pair of click handlers — every arrow press would then
       scroll two cards. The flag is on the element rather than in a Set so it dies with the node. */
    if (wrap.dataset.tsCarouselReady === '1') return;
    wrap.dataset.tsCarouselReady = '1';

    var row = wrap.querySelector('[data-ts-carousel-row]');
    var prev = wrap.querySelector('[data-ts-carousel-prev]');
    var next = wrap.querySelector('[data-ts-carousel-next]');
    var dotsEl = wrap.querySelector('[data-ts-carousel-dots]');
    if (!row || !prev || !next || !dotsEl) return;

    var label = wrap.getAttribute('data-ts-carousel-label') || 'items';

    /* The distance between two cards, taken from where the cards ACTUALLY SIT rather than rebuilt
       from width+gap. The reconstruction was wrong by half a pixel: at 1440px the flex basis resolves
       to 255.5 and the gap to 26, giving 281.5, while layout rounds the real stride to 282. Half a
       pixel is invisible on screen but it is the difference between ceil(282/282)=1 and
       ceil(282/281.5)=2, i.e. a whole extra dot that no scroll position could ever light.
       offsetLeft is also exactly what the scroll-snap points use, so the arrows land where the dots
       say they will. */
    function step() {
      if (row.children.length > 1) return row.children[1].offsetLeft - row.children[0].offsetLeft;
      var card = row.children[0];
      return card ? card.getBoundingClientRect().width : row.clientWidth;
    }

    /* ── ONE DOT PER REACHABLE POSITION, NOT ONE PER CARD ───────────────────────────────────
       One-per-card was the first attempt and it was wrong. Measured at 1440px: five cards overflow
       by 282px, which is exactly one card-plus-gap, so there are only TWO scroll positions. Three of
       the five dots could never light up no matter what you clicked — clicking dot 3 clamps to the
       end and lights dot 5 instead. A control that cannot reach its own state is worse than no
       control.
       So the count is derived from how far the row can actually travel. It changes with the
       viewport, which is why buildDots() re-runs on resize.

       This matters far more now than it did with five cards: a 16-card row at 1440px has 14
       reachable positions and 16 dots would put two permanently dead ones on the page. */
    /* ── EIGHT DOTS AT MOST, AND ON A LONG ROW EACH ONE IS A STOP ALONG THE TRAVEL ─────────────
       Dots were built for a seven-card row with two reachable positions, and anything past eight
       used to switch the strip OFF: twenty 9px dots read as a progress meter nobody can aim at, and
       each one is a 9px tap target. Measured on the live homepage 2026-09-12, that rule left ONE row
       in four with dots. At 1440px the frames row has 18 reachable positions, apparel 21 and mugs
       16, so only best sellers (7) qualified; at 375px every card is its own position, so the four
       rows have 10, 21, 24 and 19 — every strip went dark, which is what João saw on his phone.

       So the count is CAPPED rather than abandoned:
         · up to eight positions, a dot IS a position and lands exactly on a card, unchanged;
         · past eight, the eight dots are evenly spaced stops along the row's travel. Dot 1 is the
           start, dot 8 is the end, and the rest divide the distance.
       Every dot still reaches its own state, which was the original rule. What changes on a long row
       is that one dot covers several cards, so a single swipe may not move the mark: that is a
       progress meter, and it is the honest reading of a 24-card row. sync() must derive the lit dot
       the same two ways, or a click and its own highlight would disagree. */
    var MAX_DOTS = 8;

    var dots = [];
    /* true while one dot == one scroll position. Read by dotTarget() and sync(), set by buildDots()
       — the mode is a property of the CURRENT layout, so it is recomputed on every resize with the
       count it belongs to. */
    var exact = true;
    function pageCount() {
      var max = row.scrollWidth - row.clientWidth, s = step();
      if (max <= 1 || s <= 0) return 0;
      /* Every whole step the row can take, plus the start. The final position is CLAMPED to max, so
         it only counts as its own dot when it is not already a whole step away — otherwise the last
         two dots would scroll to the same place. */
      var n = Math.floor(max / s) + 1;
      if (max - Math.floor(max / s) * s > 1) n++;
      return n;
    }
    function travel() { return row.scrollWidth - row.clientWidth; }
    /* Where dot i scrolls to. Clamped in the exact case as well: the last position is short of a
       whole step, so `i * step()` would overshoot the end and the browser would clamp it silently. */
    function dotTarget(i) {
      if (exact) return Math.min(i * step(), travel());
      return dots.length > 1 ? Math.round((travel() * i) / (dots.length - 1)) : 0;
    }
    function buildDots() {
      var positions = pageCount();
      var n = Math.min(positions, MAX_DOTS);
      var isExact = n === positions;
      // nothing changed; don't churn the DOM on every resize tick. The MODE is part of "changed":
      // eight exact positions and a capped eight are the same count and different targets.
      if (n === dots.length && isExact === exact) return;
      exact = isExact;
      dotsEl.innerHTML = '';
      dots = [];
      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ts-carousel-dot';
        b.setAttribute('aria-label', tr('carousel_dot', 'Go to position {i} of {n}', { i: i + 1, n: n }));
        (function (idx) { b.addEventListener('click', function () { row.scrollTo({ left: dotTarget(idx), behavior: 'smooth' }); }); })(i);
        dotsEl.appendChild(b);
        dots.push(b);
      }
    }

    /* BOTH ARROWS STAY UP THE WHOLE TIME. They used to hide at whichever end you had reached, so
       the control you were about to reach for kept disappearing and the row jumped as it went. They
       now wrap instead (see the click handlers), which is what keeps a permanently-visible arrow
       honest: there is never a moment when pressing one does nothing.

       Still gated on `overflows`, because a row that fits entirely has genuinely nowhere to go and
       wrapping would be a scroll to the same place. `scrollWidth > clientWidth + 1` rather than a
       strict compare: sub-pixel layout leaves a fractional overflow on rows that visually fit. */
    function sync() {
      var overflows = row.scrollWidth > row.clientWidth + 1;
      var atEnd = row.scrollLeft >= row.scrollWidth - row.clientWidth - 1;
      prev.hidden = !overflows;
      next.hidden = !overflows;
      dotsEl.hidden = !overflows || dots.length === 0;

      /* The active dot is the card nearest the left edge, EXCEPT at the very end: the final scroll
         position stops short of the last card's own offset, so rounding alone would never light the
         last dot. On a CAPPED row a dot is a fraction of the travel rather than a card, so the mark
         comes from progress along that travel — the same arithmetic dotTarget() scrolls by, read
         backwards. Deriving it the other way would light a dot the click could never scroll to. */
      var i;
      if (exact) {
        i = Math.round(row.scrollLeft / step());
      } else {
        var max = travel();
        i = max > 0 ? Math.round((row.scrollLeft / max) * (dots.length - 1)) : 0;
      }
      if (atEnd) i = dots.length - 1;
      for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-on', d === i);
    }

    /* buildDots BEFORE sync, always: the count depends on how far the row can travel, so a resize
       that changes the overflow changes the number of dots, and sync would otherwise be lighting a
       dot in a list that no longer matches the layout. */
    function refresh() { buildDots(); sync(); }

    /* Wrap at the ends rather than stall. The tolerance is the same 1px used in sync(): a scroll
       position is never exactly its target after a smooth animation, so an equality test would miss
       the end and leave the arrow doing nothing on the press that matters most. */
    function maxScroll() { return row.scrollWidth - row.clientWidth; }
    prev.addEventListener('click', function () {
      if (row.scrollLeft <= 1) row.scrollTo({ left: maxScroll(), behavior: 'smooth' });
      else row.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      if (row.scrollLeft >= maxScroll() - 1) row.scrollTo({ left: 0, behavior: 'smooth' });
      else row.scrollBy({ left: step(), behavior: 'smooth' });
    });

    if (!prev.getAttribute('aria-label')) prev.setAttribute('aria-label', tr('carousel_prev', 'Previous {label}', { label: labelOf(label) }));
    if (!next.getAttribute('aria-label')) next.setAttribute('aria-label', tr('carousel_next', 'More {label}', { label: labelOf(label) }));

    row.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
    /* The cards carry lazily-loaded images, so scrollWidth is not final at parse time. With four
       rows of up to sixteen lazy cards this is no longer a nicety: at parse time most rows measure
       as if they do not overflow at all, and every control would stay hidden. */
    window.addEventListener('load', refresh);
    /* ⚠ A ROW WHOSE CARDS ARRIVE LATER. Everything above measures the row as it exists NOW, and the
       homepage's review rail is empty at boot — its cards come from a cross-origin fetch. With no
       nudge the dots stay hidden and the arrows never appear, because scrollWidth was read against
       nothing. `load` already re-measures for lazy images, but the fetch can land after it, so a
       block that fills a rail itself says so. Cheaper and more honest than a MutationObserver on
       every carousel on the page, and it is one line at the call site. */
    document.addEventListener('ts:carousel-refresh', refresh);
  }

  function boot() {
    var wraps = document.querySelectorAll('[data-ts-carousel]');
    for (var i = 0; i < wraps.length; i++) init(wraps[i]);
  }

  /* Loaded with `defer`, so the parser is done by the time this runs — but a section re-rendered
     by the theme editor is injected afterwards, and Shopify fires shopify:section:load for it.
     Re-running init() there is safe because of the data-ts-carousel-ready guard above. */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    var wraps = (e.target || document).querySelectorAll('[data-ts-carousel]');
    for (var i = 0; i < wraps.length; i++) init(wraps[i]);
  });
})();
